import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';

type TablePaginationItem = number | 'ellipsis';

@Component({
  standalone: true,
  selector: 'app-table-pagenation',
  imports: [TranslateModule, NgClass],
  templateUrl: './table-pagenation.component.html',
  styleUrl: './table-pagenation.component.scss'
})
export class TablePagenationComponent {
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 0;

  @Output() prev = new EventEmitter<void>();
  @Output() next = new EventEmitter<void>();
  @Output() goToPage = new EventEmitter<number>();

  private readonly siblingCount = 1;
  private readonly boundaryCount = 3;

  // Build pagination items (numbers + ellipsis) based on current/total
  get paginationItems(): TablePaginationItem[] {
    if (this.totalPages <= 1) {
      return [];
    }

    const pages = new Set<number>();

    for (let page = 1; page <= Math.min(this.boundaryCount, this.totalPages); page++) {
      pages.add(page);
    }

    for (
      let page = Math.max(this.totalPages - this.boundaryCount + 1, 1);
      page <= this.totalPages;
      page++
    ) {
      pages.add(page);
    }

    for (
      let page = this.currentPage - this.siblingCount;
      page <= this.currentPage + this.siblingCount;
      page++
    ) {
      if (page > 0 && page <= this.totalPages) {
        pages.add(page);
      }
    }

    const sortedPages = Array.from(pages)
      .filter(page => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: TablePaginationItem[] = [];
    let previousPage: number | null = null;

    for (const page of sortedPages) {
      if (previousPage !== null) {
        const gap = page - previousPage;

        if (gap === 2) {
          items.push(previousPage + 1);
        } else if (gap > 2) {
          items.push('ellipsis');
        }
      }

      items.push(page);
      previousPage = page;
    }

    return items;
  }

  onPrevClick(): void {
    this.prev.emit();
  }

  onNextClick(): void {
    this.next.emit();
  }

  onPageClick(page: number): void {
    this.goToPage.emit(page);
  }
}
